import { ALPHANUMERIC_REGEX, NAME_REGEX, PHONE_NUMBER_REGEX } from '@shared/constants/regex';
import { TFunction } from 'i18next';
import { z } from 'zod';
import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator';

export const refinePostalCode = (t?: TFunction) => (data: { zip: string; country: string }, ctx: z.RefinementCtx) => {
  if (!data.zip || !data.country) return;
  if (!postcodeValidatorExistsForCountry(data.country)) return;

  if (!postcodeValidator(data.zip, data.country)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t ? t('invalid_postal_code_for_selected_country') : 'Invalid postal code for selected country',
      path: ['zip'],
    });
  }
};

export const cardFormSchema = (t: TFunction) =>
  z
    .object({
      name: z
        .string({ required_error: t('name_is_required') })
        .trim()
        .min(1, t('name_is_required'))
        .regex(NAME_REGEX, t('name_cannot_contain_special_characters_or_numbers')),
      number: z
        .string({ required_error: t('card_number_is_required') })
        .trim()
        .min(1, t('card_number_is_required')),
      cvv: z
        .string({ required_error: t('security_code_is_required') })
        .trim()
        .min(1, t('security_code_is_required')),
      expiry: z
        .string({ required_error: t('expiration_date_is_required') })
        .trim()
        .min(1, t('expiration_date_is_required')),
      line1: z
        .string({ required_error: t('billing_address_is_required') })
        .trim()
        .min(1, t('billing_address_is_required')),
      state: z
        .string({ required_error: t('state_slash_province_is_required') })
        .trim()
        .min(1, t('state_slash_province_is_required'))
        .regex(NAME_REGEX, t('state_cannot_contain_special_characters_or_numbers')),
      country: z
        .string({ required_error: t('country_is_required') })
        .trim()
        .min(1, t('country_is_required')),
      zip: z
        .string({ required_error: t('postal_code_is_required') })
        .trim()
        .min(1, t('postal_code_is_required')),
    })
    .superRefine(refinePostalCode(t));

export const billingFormSchema = (t: TFunction) =>
  z
    .object({
      name: z
        .string()
        .trim()
        .min(1, t('full_name_is_required'))
        .max(150, t('full_name_cannot_be_more_than_150_characters')),
      company: z
        .string()
        .trim()
        .min(1, t('company_name_is_required'))
        .max(150, t('company_name_cannot_be_more_than_250_characters')),
      phone: z
        .string()
        .trim()
        .refine(
          (val) => {
            if (!val) return true;
            return PHONE_NUMBER_REGEX.test(val);
          },
          { message: t('invalid_phone_number_format') }
        )
        .optional()
        .nullable(),
      line1: z.string().trim().min(1, t('address_is_required')).max(45, t('address_cannot_be_more_than_45_characters')),
      state: z.string().trim().min(1, t('state_slash_province_is_required')),
      country: z.string().trim().min(1, t('country_is_required')),
      zip: z.string().trim().min(1, t('postal_code_is_required')),
      vatNumber: z
        .string()
        .trim()
        .regex(ALPHANUMERIC_REGEX, t('special_characters_are_not_allowed'))
        .optional()
        .nullable(),
    })
    .superRefine(refinePostalCode(t));
